import { MEMBER_SORT_FIELDS, MemberSortField } from '../../types';
import { SortDir, parseTableSort } from '../../../../utils/tableSort';
import { GetOrganizationMembersParams } from '../../api/useGetOrganizationMembers';
import isActiveMemberList from '../../utils/isActiveMemberList';

/** Direction each field is normally read in: names and emails A-Z, the activation date newest-first. */
const MEMBER_SORT_DEFAULT_DIR: Record<MemberSortField, SortDir> = {
    email: 'ASC',
    first_name: 'ASC',
    last_name: 'ASC',
    activated_at: 'DESC',
    // Unlike the other date, this one mostly points forward — a check-in queue reads soonest-first.
    cm_checkin_schedule_date: 'ASC'
};

/** Shared by every members-endpoint list so their headers all open on the same direction. */
export const memberSortDefaultDir = (field: MemberSortField): SortDir =>
    MEMBER_SORT_DEFAULT_DIR[field];

/**
 * Only the Active list renders a check-in column, and only its endpoint documents the ordering, so
 * a sort picked there is dropped rather than carried onto a sibling tab when the user switches.
 */
const sortFieldsFor = (listVersion: string | null): readonly MemberSortField[] =>
    isActiveMemberList(listVersion)
        ? MEMBER_SORT_FIELDS
        : MEMBER_SORT_FIELDS.filter((field) => field !== 'cm_checkin_schedule_date');

/**
 * Sorting is opt-in: with no `sort_by` in the URL both keys are omitted so the list keeps the
 * endpoint's own default ordering.
 */
export const parseMemberSort = (
    searchParams: URLSearchParams
): Pick<GetOrganizationMembersParams, 'sort_by' | 'sort_order'> => {
    const fields = sortFieldsFor(searchParams.get('list_version'));
    const sort = parseTableSort(searchParams, fields, memberSortDefaultDir);
    if (!sort) return {};

    return { sort_by: sort.field, sort_order: sort.dir === 'ASC' ? 'asc' : 'desc' };
};
